import cx from 'classnames';
import { type FC, useMemo } from 'react';
import { useLocation } from 'wouter';

import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';

import type { BannerGroup } from 'api/banner';
import Icon from 'components/Icon/Icon';
import { AuthStatus, useClientProfile } from 'store';
import pushToDataLayer, { btag } from 'utils/dataLayer';

import BannerSliderItem from './BannerSliderItem';
import filterBannersList from './filterBannersList';
import { useDotButton } from './useDotButton';
import { usePrevNextButtons } from './usePrevNextButton';

import styles from './Slider.module.scss';

type BannerSliderProps = {
  bannerGroup: BannerGroup;
  delay?: number;
};

const sliderOptions: EmblaOptionsType = {
  loop: true,
  align: 'start',
};

const BannerSlider: FC<BannerSliderProps> = ({ bannerGroup, delay }) => {
  const sliderModules = delay ? [Autoplay({ delay })] : undefined;
  const [{ data: profileData, status }] = useClientProfile();
  const isLoggedIn = status === AuthStatus.signedIn;
  const isClientResolved = status !== AuthStatus.resolving;
  const [pathname] = useLocation();

  const handleBannerItemClick = (imageUrl: string): void => {
    if (pathname === '/casino') {
      pushToDataLayer(
        'casino_banner_click',
        {
          ...(profileData?.client && {
            client: {
              id: profileData.client.id,
            },
          }),
          image_URL: imageUrl,
        },
        btag,
      );
    }

    if (pathname === '/live-casino') {
      pushToDataLayer(
        'live_casino_banner_click',
        {
          ...(profileData?.client && {
            client: {
              id: profileData.client.id,
            },
          }),
          image_URL: imageUrl,
        },
        btag,
      );
    }
  };

  const [sliderRef, sliderApi] = useEmblaCarousel(sliderOptions, sliderModules);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(sliderApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(sliderApi);

  const filteredBanners = useMemo(
    () =>
      isClientResolved
        ? bannerGroup && filterBannersList(bannerGroup.banners, isLoggedIn)
        : [],
    [bannerGroup, isLoggedIn, isClientResolved],
  );

  if (filteredBanners.length < 1) {
    return null;
  }

  return (
    <div key={bannerGroup.id} className={styles.slider}>
      <div ref={sliderRef}>
        <div className={styles['slider-container']}>
          {filteredBanners.map((banner) => (
            <BannerSliderItem
              key={banner.id}
              actionUrl={banner.action_url}
              actionUrlTarget={banner.action_url_target}
              imageUrl={banner.image_url}
              description={banner.description}
              onClick={() => handleBannerItemClick(banner.image_url)}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        className={styles['slider-button-prev']}
        onClick={onPrevButtonClick}
        disabled={prevBtnDisabled}
      >
        <Icon name="arrow-circle-right" />
      </button>
      <button
        type="button"
        className={styles['slider-button-next']}
        onClick={onNextButtonClick}
        disabled={nextBtnDisabled}
      >
        <Icon name="arrow-circle-right" />
      </button>
      <div className={styles['slider-dots']}>
        {scrollSnaps.map((value, index) => (
          <button
            type="button"
            key={index + value}
            onClick={() => onDotButtonClick(index)}
            className={cx(styles['slider-dot'], {
              [styles['slider-dot-selected']]: index === selectedIndex,
            })}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerSlider;
